/**
 * FeedbackWidget.tsx
 * Floating feedback button + intake dialog.
 *
 * Conditionally mounted by App.tsx via useFeedbackGate — never rendered for
 * a normal customer (SPEC-feedback-001 §4.2).
 *
 * Captures: free-text fields, area picker (nav sections), severity, optional
 * screenshot (with preview + remove/retake), console + network diagnostics,
 * and auto-context (route, app SHA, browser, OS).
 */

import { useState, useCallback, useRef, useEffect } from 'react'
import {
  Fab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Select,
  FormControl,
  FormHelperText,
  Box,
  Typography,
  CircularProgress,
  Alert,
  Tooltip,
  Chip,
  Divider,
} from '@mui/material'
import FeedbackOutlinedIcon from '@mui/icons-material/FeedbackOutlined'
import CameraAltOutlinedIcon from '@mui/icons-material/CameraAltOutlined'
import CloseIcon from '@mui/icons-material/Close'
import ReplayIcon from '@mui/icons-material/Replay'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { useLocation, useNavigate } from 'react-router-dom'
import html2canvas from 'html2canvas'
import { feedbackApi, type FeedbackArea, type FeedbackSeverity } from '../../services/feedbackApi'
import {
  initDiagnosticsBuffer,
  onRouteChange,
  getDiagnostics,
} from './diagnosticsBuffer'
import { useDraggableFab } from './useDraggableFab'

// ─── constants ────────────────────────────────────────────────────────────────

export const AREA_OPTIONS: { value: FeedbackArea; label: string }[] = [
  { value: 'programs',         label: 'Programs'         },
  { value: 'database-sources', label: 'Database sources' },
  { value: 'usage',            label: 'Usage'            },
  { value: 'workbench',        label: 'Workbench'        },
  { value: 'docs',             label: 'Docs'             },
  { value: 'org-settings',     label: 'Org Settings'     },
]

const SEVERITY_OPTIONS: { value: FeedbackSeverity; label: string; color: 'default' | 'warning' | 'error' }[] = [
  { value: 'low',      label: 'Low',      color: 'default' },
  { value: 'normal',   label: 'Normal',   color: 'warning' },
  { value: 'blocking', label: 'Blocking', color: 'error'   },
]

/** Max screenshot size in bytes (1 MB base64 ≈ 750 KB raw). */
const SCREENSHOT_MAX_BYTES = 1_000_000

// ─── route → area map ─────────────────────────────────────────────────────────

/**
 * Deterministically maps the current pathname to a FeedbackArea. Returns
 * undefined when no match — the user must pick manually.
 */
export function detectAreaFromPath(pathname: string): FeedbackArea | undefined {
  if (/^\/(workspaces|projects|runs)(\/|$)/.test(pathname)) return 'programs'
  if (/^\/estate(\/|$)/.test(pathname))                      return 'database-sources'
  if (/^\/analytics(\/|$)/.test(pathname))                   return 'usage'
  if (/^\/ai-canvas(\/|$)/.test(pathname))                   return 'workbench'
  if (/^\/knowledge-base(\/|$)/.test(pathname))              return 'docs'
  if (/^\/org-settings(\/|$)/.test(pathname))                return 'org-settings'
  return undefined
}

// ─── helpers ──────────────────────────────────────────────────────────────────

function generateIdempotencyKey(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`
}

function getBrowserInfo(): string {
  const ua = navigator.userAgent
  if (ua.includes('Firefox'))  return `Firefox ${ua.match(/Firefox\/([\d.]+)/)?.[1] ?? ''}`
  if (ua.includes('Edg'))      return `Edge ${ua.match(/Edg\/([\d.]+)/)?.[1] ?? ''}`
  if (ua.includes('Chrome'))   return `Chrome ${ua.match(/Chrome\/([\d.]+)/)?.[1] ?? ''}`
  if (ua.includes('Safari'))   return `Safari ${ua.match(/Version\/([\d.]+)/)?.[1] ?? ''}`
  return navigator.userAgent.slice(0, 60)
}

function getOSInfo(): string {
  const ua = navigator.userAgent
  if (ua.includes('Windows')) return 'Windows'
  if (ua.includes('Mac'))     return 'macOS'
  if (ua.includes('Linux'))   return 'Linux'
  if (ua.includes('Android')) return 'Android'
  if (ua.includes('iOS'))     return 'iOS'
  return 'Unknown'
}

const APP_SHA = import.meta.env['VITE_APP_SHA'] ?? 'unknown'

// ─── sub-components ───────────────────────────────────────────────────────────

interface SectionLabelProps { children: React.ReactNode }
function SectionLabel({ children }: SectionLabelProps) {
  return (
    <Typography
      variant="overline"
      sx={{
        fontSize: '10px',
        fontWeight: 700,
        letterSpacing: '0.08em',
        color: 'text.disabled',
        lineHeight: 1,
        mb: 0.75,
        display: 'block',
      }}
    >
      {children}
    </Typography>
  )
}

// ─── screenshot control ───────────────────────────────────────────────────────

interface ScreenshotControlProps {
  screenshot: string | undefined
  screenshotError: string | null
  onCapture: () => void
  onRemove: () => void
}

export function ScreenshotControl({ screenshot, screenshotError, onCapture, onRemove }: ScreenshotControlProps) {
  return (
    <Box>
      <SectionLabel>Screenshot</SectionLabel>

      {screenshot ? (
        <Box>
          <Box
            sx={{
              position: 'relative',
              display: 'inline-block',
              borderRadius: 1.5,
              overflow: 'hidden',
              border: '1px solid',
              borderColor: 'divider',
              mb: 1,
              width: '100%',
            }}
          >
            <img
              src={screenshot}
              alt="Captured screenshot preview"
              data-testid="screenshot-preview"
              style={{
                display: 'block',
                width: '100%',
                maxHeight: 160,
                objectFit: 'contain',
                backgroundColor: '#f5f5f5',
              }}
            />
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              size="small"
              variant="outlined"
              color="inherit"
              startIcon={<CloseIcon sx={{ fontSize: 14 }} />}
              onClick={onRemove}
              data-testid="screenshot-remove"
              sx={{ fontSize: '12px', py: 0.5 }}
            >
              Remove
            </Button>
            <Button
              size="small"
              variant="outlined"
              startIcon={<ReplayIcon sx={{ fontSize: 14 }} />}
              onClick={onCapture}
              data-testid="screenshot-retake"
              sx={{ fontSize: '12px', py: 0.5 }}
            >
              Retake
            </Button>
          </Box>
        </Box>
      ) : (
        <Button
          size="small"
          variant="outlined"
          startIcon={<CameraAltOutlinedIcon />}
          onClick={onCapture}
          data-testid="screenshot-capture"
          sx={{ mb: screenshotError ? 0.5 : 0 }}
        >
          Add screenshot
        </Button>
      )}

      {screenshotError && (
        <Typography variant="caption" color="error" sx={{ mt: 0.75, display: 'block' }}>
          {screenshotError}
        </Typography>
      )}

      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.75, display: 'block' }}>
        Optional. Screenshots may contain client schema names or data — shared only with the
        internal team.
      </Typography>
    </Box>
  )
}

// ─── diagnostics indicator ────────────────────────────────────────────────────

function DiagnosticsIndicator() {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 0.75,
        p: 1.25,
        borderRadius: 1.5,
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: 'action.hover',
      }}
    >
      <Tooltip
        title="Console messages and network request metadata (method, URL, status, timing) for this page are captured. Credentials and tokens are redacted before storage. Bodies are never captured."
        placement="top"
        arrow
      >
        <InfoOutlinedIcon sx={{ fontSize: 15, color: 'text.disabled', mt: '1px', flexShrink: 0 }} />
      </Tooltip>
      <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.4 }}>
        Console + network logs for this page will be attached (redacted, internal-only).
      </Typography>
    </Box>
  )
}

// ─── form state ───────────────────────────────────────────────────────────────

interface FormState {
  area: FeedbackArea | ''
  severity: FeedbackSeverity
  whatHappened: string
  whatExpected: string
}

const INITIAL_FORM: FormState = {
  area: '',
  severity: 'normal',
  whatHappened: '',
  whatExpected: '',
}

// ─── main component ───────────────────────────────────────────────────────────

export interface FeedbackWidgetProps {
  /** Keeps the legacy draggable launcher available for explicit pop-out use. */
  launcher?: 'floating' | 'none'
  /** Increment to open the dialog from an external control such as the app nav. */
  openSignal?: number
}

export function FeedbackWidget({ launcher = 'floating', openSignal }: FeedbackWidgetProps) {
  const location = useLocation()
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  const [form, setForm] = useState<FormState>(INITIAL_FORM)
  const [screenshot, setScreenshot] = useState<string | undefined>(undefined)
  const [screenshotError, setScreenshotError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [successTicket, setSuccessTicket] = useState<string | number | null>(null)
  const idempotencyKeyRef = useRef<string>(generateIdempotencyKey())
  const lastOpenSignalRef = useRef(openSignal)
  const fabDrag = useDraggableFab()

  // Init diagnostics buffer once (idempotent — safe if called multiple times)
  useEffect(() => {
    initDiagnosticsBuffer()
  }, [])

  // Clear buffers on route change so each page is captured independently
  useEffect(() => {
    onRouteChange(location.pathname + location.search)
  }, [location.pathname, location.search])

  // Validation
  const errors = {
    area: form.area === '' ? 'Required' : null,
    whatHappened: form.whatHappened.trim() === '' ? 'Required' : null,
    whatExpected: form.whatExpected.trim() === '' ? 'Required' : null,
  }
  const isValid = !errors.area && !errors.whatHappened && !errors.whatExpected

  const handleOpen = useCallback(() => {
    idempotencyKeyRef.current = generateIdempotencyKey()
    setSubmitError(null)
    setSuccessTicket(null)

    // Auto-detect area from current route; leave blank if no match
    const detectedArea = detectAreaFromPath(location.pathname)
    setForm({ ...INITIAL_FORM, area: detectedArea ?? '' })

    setOpen(true)
  }, [location.pathname])

  useEffect(() => {
    if (openSignal === undefined || openSignal === lastOpenSignalRef.current) return
    lastOpenSignalRef.current = openSignal
    handleOpen()
  }, [handleOpen, openSignal])

  const handleClose = () => {
    if (submitting) return
    setOpen(false)
    setTimeout(() => {
      setForm(INITIAL_FORM)
      setScreenshot(undefined)
      setScreenshotError(null)
      setSubmitError(null)
      setSuccessTicket(null)
    }, 200)
  }

  const handleScreenshot = useCallback(async () => {
    setScreenshotError(null)
    try {
      const canvas = await html2canvas(document.body, {
        logging: false,
        scale: 0.6,
        useCORS: true,
        backgroundColor: null,
        ignoreElements: (el) =>
          el.classList?.contains('MuiDialog-root') ||
          el.classList?.contains('MuiBackdrop-root') ||
          el.getAttribute?.('data-feedback-widget') === 'true',
      })

      const dataUrl = canvas.toDataURL('image/jpeg', 0.7)
      if (dataUrl.length > SCREENSHOT_MAX_BYTES) {
        setScreenshotError('Screenshot too large — try again.')
        return
      }
      setScreenshot(dataUrl)
    } catch {
      setScreenshotError('Could not capture the screen. Try again.')
    }
  }, [])

  const handleSubmit = async () => {
    if (!isValid || submitting) return
    setSubmitting(true)
    setSubmitError(null)

    const currentRoute = location.pathname + location.search

    try {
      const result = await feedbackApi.submit({
        area: form.area as FeedbackArea,
        severity: form.severity,
        whatHappened: form.whatHappened.trim(),
        whatExpected: form.whatExpected.trim(),
        context: {
          route: currentRoute,
          appSha: APP_SHA,
          browser: getBrowserInfo(),
          os: getOSInfo(),
        },
        screenshot,
        diagnostics: getDiagnostics(currentRoute),
        idempotencyKey: idempotencyKeyRef.current,
      })
      setSuccessTicket(result.ticketId)
    } catch (err) {
      setSubmitError(
        err instanceof Error ? err.message : 'Something went wrong. Please try again.'
      )
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <>
      {launcher === 'floating' && (
        <Tooltip title="Send feedback — drag to move" placement="left">
          <Fab
            ref={fabDrag.ref}
            size="small"
            color="primary"
            aria-label="Send feedback"
            onClick={handleOpen}
            onPointerDown={fabDrag.onPointerDown}
            onClickCapture={fabDrag.onClickCapture}
            sx={{
              position: 'fixed',
              ...fabDrag.style,
              zIndex: (theme) => theme.zIndex.speedDial,
              opacity: 0.85,
              cursor: 'grab',
              touchAction: 'none',
              '&:active': { cursor: 'grabbing' },
              '&:hover': { opacity: 1 },
            }}
          >
            <FeedbackOutlinedIcon fontSize="small" />
          </Fab>
        </Tooltip>
      )}

      {/* Intake dialog — lg width, two-column layout, no scroll */}
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="lg"
        fullWidth
        aria-labelledby="feedback-dialog-title"
        data-feedback-widget="true"
        PaperProps={{
          sx: {
            borderRadius: 2.5,
            maxWidth: 960,
          },
        }}
      >
        <DialogTitle
          id="feedback-dialog-title"
          sx={{
            fontWeight: 700,
            fontSize: '17px',
            pb: 1,
            pt: 2.5,
            px: 3,
          }}
        >
          Send feedback
          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 400, mt: 0.25 }}>
            Help us improve Archer — tell us what you saw and what you expected.
          </Typography>
        </DialogTitle>

        <Divider />

        <DialogContent sx={{ px: 3, py: 2.5 }}>
          {/* Success state */}
          {successTicket !== null ? (
            <Box sx={{ py: 1 }}>
              <Alert severity="success">
                Logged — ticket #{successTicket}. Track it in{' '}
                <Typography
                  component="button"
                  type="button"
                  onClick={() => {
                    handleClose()
                    navigate('/feedback/mine')
                  }}
                  sx={{
                    color: 'inherit',
                    fontWeight: 700,
                    background: 'none',
                    border: 'none',
                    p: 0,
                    cursor: 'pointer',
                    textDecoration: 'underline',
                    font: 'inherit',
                  }}
                >
                  My Feedback
                </Typography>
                .
              </Alert>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', gap: 3 }}>
              {/* ── Left column ── */}
              <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                {submitError && (
                  <Alert severity="error" onClose={() => setSubmitError(null)}>
                    {submitError}
                  </Alert>
                )}

                {/* Row: Area + Severity side by side */}
                <Box sx={{ display: 'flex', gap: 2 }}>
                  {/* Area — overline label only, no MUI floating label */}
                  <Box sx={{ flex: 1 }}>
                    <SectionLabel>Area *</SectionLabel>
                    <FormControl size="small" fullWidth required error={!!errors.area}>
                      <Select
                        displayEmpty
                        value={form.area}
                        onChange={(e) => setForm((f) => ({ ...f, area: e.target.value as FeedbackArea }))}
                        inputProps={{ 'aria-label': 'Area' }}
                        sx={{ fontSize: '13px' }}
                        renderValue={(val) => {
                          if (!val) return <Typography sx={{ fontSize: '13px', color: 'text.disabled' }}>Select area…</Typography>
                          return AREA_OPTIONS.find((o) => o.value === val)?.label ?? val
                        }}
                      >
                        {AREA_OPTIONS.map((o) => (
                          <MenuItem key={o.value} value={o.value} sx={{ fontSize: '13px' }}>{o.label}</MenuItem>
                        ))}
                      </Select>
                      {errors.area && <FormHelperText>{errors.area}</FormHelperText>}
                    </FormControl>
                  </Box>

                  {/* Severity — overline label only, no MUI floating label */}
                  <Box sx={{ flex: 1 }}>
                    <SectionLabel>Severity</SectionLabel>
                    <FormControl size="small" fullWidth>
                      <Select
                        displayEmpty
                        value={form.severity}
                        onChange={(e) =>
                          setForm((f) => ({ ...f, severity: e.target.value as FeedbackSeverity }))
                        }
                        inputProps={{ 'aria-label': 'Severity' }}
                        sx={{ fontSize: '13px' }}
                      >
                        {SEVERITY_OPTIONS.map((o) => (
                          <MenuItem key={o.value} value={o.value} sx={{ fontSize: '13px' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Chip label={o.label} color={o.color} size="small" />
                            </Box>
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>
                </Box>

                {/* What happened */}
                <Box>
                  <SectionLabel>What happened *</SectionLabel>
                  <TextField
                    placeholder="Describe the issue. What did you click, what went wrong?"
                    multiline
                    minRows={4}
                    fullWidth
                    required
                    size="small"
                    value={form.whatHappened}
                    onChange={(e) => setForm((f) => ({ ...f, whatHappened: e.target.value }))}
                    error={!!errors.whatHappened && form.whatHappened !== ''}
                    helperText={form.whatHappened === '' ? '' : errors.whatHappened ?? ''}
                    inputProps={{ maxLength: 2000, 'aria-label': 'What happened' }}
                    sx={{ '& .MuiInputBase-input': { fontSize: '13px' } }}
                  />
                </Box>

                {/* What expected */}
                <Box>
                  <SectionLabel>What did you expect? *</SectionLabel>
                  <TextField
                    placeholder="What should have happened instead?"
                    multiline
                    minRows={3}
                    fullWidth
                    required
                    size="small"
                    value={form.whatExpected}
                    onChange={(e) => setForm((f) => ({ ...f, whatExpected: e.target.value }))}
                    error={!!errors.whatExpected && form.whatExpected !== ''}
                    helperText={form.whatExpected === '' ? '' : errors.whatExpected ?? ''}
                    inputProps={{ maxLength: 2000, 'aria-label': 'What did you expect' }}
                    sx={{ '& .MuiInputBase-input': { fontSize: '13px' } }}
                  />
                </Box>
              </Box>

              {/* ── Right column ── */}
              <Box
                sx={{
                  width: 300,
                  flexShrink: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 2.5,
                }}
              >
                {/* Screenshot */}
                <ScreenshotControl
                  screenshot={screenshot}
                  screenshotError={screenshotError}
                  onCapture={handleScreenshot}
                  onRemove={() => setScreenshot(undefined)}
                />

                {/* Diagnostics indicator */}
                <DiagnosticsIndicator />

                {/* Auto-context note */}
                <Typography variant="caption" color="text.disabled" sx={{ lineHeight: 1.4 }}>
                  Auto-captured: current screen, app version ({APP_SHA.slice(0, 7)}), browser, and OS.
                  No action needed.
                </Typography>
              </Box>
            </Box>
          )}
        </DialogContent>

        <Divider />

        <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
          <Button onClick={handleClose} color="inherit" disabled={submitting} sx={{ fontSize: '13px' }}>
            {successTicket !== null ? 'Close' : 'Cancel'}
          </Button>
          {successTicket === null && (
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={!isValid || submitting}
              startIcon={submitting ? <CircularProgress size={14} color="inherit" /> : undefined}
              sx={{ fontSize: '13px', minWidth: 130 }}
            >
              {submitting ? 'Sending…' : 'Send feedback'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  )
}
